import type { BackgroundMode } from './backgroundProcessor';
import type { FaceMaskId } from './faceMasks';

/**
 * Video-effect presets for the camera composite pipeline: the background blur
 * strength (how aggressively the background copy is blurred) and the whole-frame
 * color filters. Pure mapping tables only - the compositing application lives in
 * backgroundProcessor.ts and the persistence in room/devicePrefs.ts.
 *
 * Both settings persist in the v2 device-preferences document and apply live:
 * the prejoin preview pipeline and the in-call machine (room/cameraEffects.ts)
 * both drive them without a rejoin.
 */

/** Background blur strength presets, in UI order («Слабое / Среднее / Сильное»). */
export const BLUR_STRENGTHS = ['light', 'medium', 'strong'] as const;

/** One background blur strength preset. */
export type BlurStrength = (typeof BLUR_STRENGTHS)[number];

/**
 * Multiplier of the quality level's base blur radius per strength preset.
 * "medium" is 1: the S1 default look, so existing users see no change. The
 * strong end is bounded at 1.8x (the perf guard below keeps the mapping within
 * the quality budget), light clearly lightens today's default at 0.55x.
 */
export const BLUR_STRENGTH_SCALE: Readonly<Record<BlurStrength, number>> = {
  light: 0.55,
  medium: 1,
  strong: 1.8,
};

/**
 * Canvas blur radius (px) for a quality level's base radius and a strength
 * preset. Never below 2 px (a smaller blur is invisible but still costs a
 * filter pass) and never above 2x the base: at the "low" tier the strong
 * preset then stays inside the tier's composite budget instead of pushing
 * frames past the capture rate (auto-quality semantics downgrade via the tier
 * itself - the mapping never adds cost the tier did not budget for).
 */
export function blurRadiusPx(basePx: number, strength: BlurStrength): number {
  return Math.max(2, Math.min(Math.round(basePx * BLUR_STRENGTH_SCALE[strength]), basePx * 2));
}

/** Whole-frame color filter presets, in UI order («Нет/Яркий/Тёплый/Холодный/Ч/Б/Сепия»). */
export const VIDEO_FILTERS = ['none', 'bright', 'warm', 'cool', 'mono', 'sepia'] as const;

/** One whole-frame color filter preset. */
export type VideoFilter = (typeof VIDEO_FILTERS)[number];

/**
 * The canvas `ctx.filter` string per preset, applied to the composited frame
 * (person + background) as one final self-draw pass. Values are tuned to be
 * clearly distinguishable at a glance while keeping faces natural: "warm"
 * leans on sepia+saturate with a slight red hue shift, "cool" on a blue hue
 * shift with a touch of brightness, "mono" is a contrast-boosted grayscale.
 * "none" is the literal "none" filter: the pass is skipped entirely.
 */
export const VIDEO_FILTER_CSS: Readonly<Record<VideoFilter, string>> = {
  none: 'none',
  bright: 'saturate(1.35) contrast(1.12) brightness(1.06)',
  warm: 'sepia(0.32) saturate(1.3) hue-rotate(-14deg)',
  cool: 'saturate(1.15) hue-rotate(16deg) brightness(1.04)',
  mono: 'grayscale(1) contrast(1.1)',
  sepia: 'sepia(0.62) saturate(1.2) contrast(1.02)',
};

/** True when a color filter preset modifies the frame. */
export function filterIsActive(filter: VideoFilter): boolean {
  return filter !== 'none';
}

/**
 * True when the composite pipeline must run for the chosen treatment: a
 * background effect, an active color filter OR an active face mask. The "raw
 * camera" choice alone bypasses the canvas (zero processing cost); a filter or
 * mask alone routes the raw frame through the composite - the documented cost
 * of those combinations is one full-frame draw + (filter) one filter self-draw
 * or (mask) one landmark-inference + sprite blit per frame (no segmentation:
 * the processor skips person-mask inference when no background effect is
 * selected; a mask needs landmarks only).
 */
export function effectsNeedPipeline(
  mode: BackgroundMode,
  filter: VideoFilter,
  mask: FaceMaskId = 'none',
): boolean {
  return mode !== 'none' || filterIsActive(filter) || mask !== 'none';
}
